import React, { useMemo } from 'react';
import { IAppointmentTypeData } from '../types';
import {
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Grid,
  Typography
} from '@mui/material';


interface AppointmentTypeTableProps {
  data: IAppointmentTypeData[];
  showStats?: boolean;
}

const StatsCard = ({ title, value }: { title: string; value: number | string }) => (
  <Card className="h-full">
    <CardContent>
      <Typography color="textSecondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h5" component="div">
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const AppointmentTypeTable = ({ 
  data,
  showStats = true
}: AppointmentTypeTableProps) => {
  // Calculate summary statistics
  const stats = useMemo(() => {
    const totalAppointments = data.reduce((sum, item) => sum + item.totalAppointments, 0);
    const uniqueDepartments = Array.from(new Set(data.map(item => item.departmentName)));
    const uniqueAppointmentTypes = Array.from(new Set(data.map(item => item.appointmentType)));
    const avgWaitDays = data.reduce((sum, item) => sum + item.avgDaysUntilAppointment * item.totalAppointments, 0) / totalAppointments;
    
    return {
      totalAppointments,
      totalDepartments: uniqueDepartments.length,
      totalAppointmentTypes: uniqueAppointmentTypes.length,
      avgWaitDays: avgWaitDays.toFixed(1)
    };
  }, [data]);

  // Transform data for the table
  const tableData = useMemo(() => {
    // Get unique department names and appointment types
    const departments = Array.from(new Set(data.map(item => item.departmentName)));
    const appointmentTypes = Array.from(new Set(data.map(item => item.appointmentType)));
    
    // Create a map for quick lookup
    const dataMap = new Map();
    data.forEach(item => {
      const key = `${item.departmentName}-${item.appointmentType}`;
      dataMap.set(key, item.totalAppointments);
    });
    
    // Calculate department totals for the last column
    const departmentTotals = departments.reduce((acc, dept) => {
      acc[dept] = appointmentTypes.reduce((sum, type) => {
        const key = `${dept}-${type}`;
        return sum + (dataMap.get(key) || 0);
      }, 0);
      return acc;
    }, {});

    // Calculate average wait times for each department
    const departmentWaitTimes = departments.reduce((acc, dept) => {
      const deptData = data.filter(item => item.departmentName === dept);
      const totalAppointments = deptData.reduce((sum, item) => sum + item.totalAppointments, 0);
      const weightedWaitTime = deptData.reduce((sum, item) => 
        sum + (Math.max(0, item.avgDaysUntilAppointment) * item.totalAppointments), 0);
      acc[dept] = totalAppointments > 0 ? Math.round(weightedWaitTime / totalAppointments) : '0';
      return acc;
    }, {});
    
    // Calculate appointment type totals for the last row
    const typeTotals = appointmentTypes.reduce((acc, type) => {
      acc[type] = departments.reduce((sum, dept) => {
        const key = `${dept}-${type}`;
        return sum + (dataMap.get(key) || 0);
      }, 0);
      return acc;
    }, {});
    
    return { departments, appointmentTypes, dataMap, departmentTotals, typeTotals, departmentWaitTimes };
  }, [data]);
  
  return (
    <div className="space-y-4">
      {/* Stats Summary Cards */}
      {showStats && (
        <Grid container spacing={2} className="mb-4">
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard 
              title="Total Appointments" 
              value={stats.totalAppointments} 
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard 
              title="Departments" 
              value={stats.totalDepartments} 
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard 
              title="Appointment Types" 
              value={stats.totalAppointmentTypes} 
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard 
              title="Avg. Wait (Days)" 
              value={stats.avgWaitDays} 
            />
          </Grid>
        </Grid>
      )}

      {/* Cross-tabulation Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Department / Type</strong></TableCell>
              {tableData.appointmentTypes.map(type => (
                <TableCell key={type} align="right"><strong>{type}</strong></TableCell>
              ))}
              <TableCell align="right"><strong>Total</strong></TableCell>
              <TableCell align="right"><strong>Avg Wait (Days)</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.departments.map(dept => (
              <TableRow key={dept}>
                <TableCell component="th" scope="row">
                  <strong>{dept}</strong>
                </TableCell>
                {tableData.appointmentTypes.map(type => {
                  const key = `${dept}-${type}`;
                  return (
                    <TableCell key={`${dept}-${type}`} align="right">
                      {tableData.dataMap.get(key) || 0}
                    </TableCell>
                  );
                })}
                <TableCell align="right"><strong>{tableData.departmentTotals[dept]}</strong></TableCell>
                <TableCell align="right"><strong>{tableData.departmentWaitTimes[dept]}</strong></TableCell>
              </TableRow>
            ))}
            {/* Totals row */}
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell component="th" scope="row">
                <strong>Total</strong>
              </TableCell>
              {tableData.appointmentTypes.map(type => (
                <TableCell key={`total-${type}`} align="right">
                  <strong>{tableData.typeTotals[type]}</strong>
                </TableCell>
              ))}
              <TableCell align="right">
                <strong>{stats.totalAppointments}</strong>
              </TableCell>
              <TableCell align="right">
                <strong>{stats.avgWaitDays}</strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default AppointmentTypeTable;